import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import ghostIcon from '@/assets/ghost.png'
import { COLORS, GHOST_BTN_STYLE, PRIMARY_BTN_STYLE, STEPS } from './shared'

export default function OnboardingShell({
  currentStep,
  steps = STEPS,
  children,
  onBack,
  onNext,
  onSkip,
  onExit,
  nextLabel = 'Continuar',
  nextDisabled = false,
  nextLoading = false,
  hideNext = false,
  hideBack = false,
  hideFooter = false,
}) {
  const step = steps[currentStep] ?? steps[0]
  const total = steps.length
  const isFirst = currentStep === 0
  const isLast = currentStep === total - 1
  const progress = ((currentStep + 1) / total) * 100

  return (
    <div
      className="fixed inset-0 z-[900] flex flex-col overflow-hidden"
      style={{ background: COLORS.bg, fontFamily: 'Sora, sans-serif' }}
    >
      {/* Top progress bar */}
      <div
        className="relative h-[2px] shrink-0 overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.05)' }}
      >
        <div
          className="absolute inset-y-0 left-0 transition-all duration-500"
          style={{
            width: `${progress}%`,
            background:
              'linear-gradient(90deg, #AD34FE 0%, #7b1fd4 50%, #34ADFE 100%)',
            boxShadow: '0 0 12px rgba(173,52,254,0.45)',
          }}
        />
      </div>

      {/* Header */}
      <header className="flex shrink-0 items-center justify-between px-4 py-4 sm:px-8 sm:py-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div
              aria-hidden
              className="absolute inset-0 rounded-full blur-lg"
              style={{ background: '#AD34FE', opacity: 0.35 }}
            />
            <img
              src={ghostIcon}
              alt=""
              className="relative h-8 w-8 object-contain sm:h-9 sm:w-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-[10px] font-semibold uppercase tracking-[0.18em] sm:text-[11px]"
              style={{ color: 'rgba(255,255,255,0.35)' }}
            >
              {currentStep + 1} / {total}
            </span>
            <span
              className="h-3 w-px"
              style={{ background: 'rgba(255,255,255,0.12)' }}
            />
            <span
              className="truncate text-[11px] font-semibold uppercase tracking-[0.16em] sm:text-xs"
              style={{ color: 'rgba(173,52,254,0.9)' }}
            >
              {step.title}
            </span>
          </div>
        </div>

        {onExit && !isLast && (
          <button
            type="button"
            onClick={onExit}
            className="flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-[11px] font-medium transition-all duration-200 hover:bg-white/5 sm:px-3 sm:text-xs"
            style={{ color: 'rgba(255,255,255,0.5)' }}
          >
            <X className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Sair</span>
          </button>
        )}
      </header>

      {/* Body — scrollable */}
      <main className="relative flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex min-h-full items-start justify-center px-4 py-6 sm:px-8 sm:py-10 lg:items-center lg:py-12">
          <div key={currentStep} className="onboarding-step-enter w-full">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      {!hideFooter && (
        <footer
          className="shrink-0 border-t px-4 py-3 sm:px-8 sm:py-4"
          style={{
            borderColor: 'rgba(255,255,255,0.05)',
            background: 'rgba(10, 6, 18, 0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          <div className="mx-auto flex max-w-4xl items-center justify-between gap-2 sm:gap-4">
            <div className="flex items-center">
              {!hideBack && !isFirst && (
                <button
                  type="button"
                  onClick={onBack}
                  className="flex h-10 items-center gap-1.5 rounded-xl px-3 text-xs font-medium transition-all duration-200 hover:bg-white/5 sm:h-11 sm:gap-2 sm:px-5 sm:text-sm"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Voltar</span>
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {step.optional && onSkip && (
                <button
                  type="button"
                  onClick={onSkip}
                  className="flex h-10 items-center rounded-xl px-3 text-xs font-medium transition-all duration-200 hover:bg-white/5 sm:h-11 sm:px-5 sm:text-sm"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                >
                  <span className="sm:hidden">Pular</span>
                  <span className="hidden sm:inline">Pular por agora</span>
                </button>
              )}
              {!hideNext && (
                <button
                  type="button"
                  onClick={onNext}
                  disabled={nextDisabled || nextLoading}
                  className="group flex h-10 items-center gap-1.5 rounded-xl px-4 text-xs font-semibold transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed sm:h-11 sm:gap-2 sm:px-6 sm:text-sm"
                  style={
                    nextDisabled
                      ? { ...GHOST_BTN_STYLE, color: 'rgba(255,255,255,0.4)' }
                      : PRIMARY_BTN_STYLE
                  }
                >
                  {nextLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Aguarde
                    </span>
                  ) : (
                    <>
                      <span className="truncate">{nextLabel}</span>
                      <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </footer>
      )}

      <style>{`
        @keyframes onboarding-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .onboarding-step-enter {
          animation: onboarding-fade-in 0.45s cubic-bezier(0.22, 1, 0.36, 1);
        }
      `}</style>
    </div>
  )
}
